import { useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { tokens } from "@core/theme/tokens";
import { useSession } from "@core/session/SessionProvider";
import { Sidebar } from "./Sidebar";
import {
  IdleSessionGuard,
  consumeReturnPath,
  saveReturnPath,
} from "./IdleSessionGuard";

// Padding de `<main>`. Vivía en `StickyScreenHeader.tsx` porque ese
// componente lo necesitaba para sangrar en negativo y volver a aplicarlo
// adentro de su propia caja sticky; al revertirse STK-01 (revisión en vivo
// 2026-09-22) `StickyScreenHeader` se eliminó por quedar sin consumidores, y
// este valor —que sigue haciendo falta acá, sin relación con lo sticky—
// volvió a vivir junto a su único uso.
const PORTAL_MAIN_PADDING = {
  top: 30,
  x: 38,
  bottom: 60,
} as const;

// Extraído de admin.jsx:Admin (:3786) por SPEC-201 / components.md §3.1.
export function PortalShell() {
  const { isLoading, isError, session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPathConsumed = useRef(false);

  // SPEC-201 §3 / criterio de aceptación 5: al expirar y volver a entrar, el
  // portal retoma la ruta anterior. IdleSessionGuard guarda esa ruta antes de
  // cerrar sesión (sessionStorage); acá, al montar el shell con una sesión ya
  // vigente (login nuevo tras la expiración), se consume una única vez y se
  // navega ahí si existe.
  useEffect(() => {
    if (!session || returnPathConsumed.current) return;
    returnPathConsumed.current = true;
    const returnPath = consumeReturnPath();
    if (returnPath) {
      navigate(returnPath, { replace: true });
    }
  }, [session, navigate]);

  // FINDING: SPEC-201 describe el shell asumiendo una sesión ya vigente y
  // no cubre el caso de visita directa sin sesión (ej. abrir "/" sin haber
  // pasado por el login). Antes de SPEC-221, "/access" era un placeholder,
  // así que este caso no era alcanzable de verdad. Ahora sí lo es: sin este
  // redirect, cualquier visita sin sesión quedaba en un mensaje sin salida.
  // Mismo mecanismo de retención de ruta que usa IdleSessionGuard al expirar.
  useEffect(() => {
    if (isLoading || session) return;
    saveReturnPath(location.pathname + location.search);
    navigate("/access", { replace: true });
  }, [isLoading, session, location, navigate]);

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          color: tokens.text.tertiary,
        }}
      >
        Cargando…
      </div>
    );
  }

  if (isError || !session) {
    return null;
  }

  return (
    <IdleSessionGuard idleMinutes={session.sessionIdleMinutes}>
      <div style={{ display: "flex", minHeight: "100vh" }}>
        <Sidebar />
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {/* Acá vivían los chips «Validador QR» y «Volver a la app», en un
            bloque `position: absolute` con `zIndex: 20`. Se eliminaron en la
            revisión en vivo del 2026-09-22 (punto #5): no eran funciones del
            producto sino restos de la navegación entre las tres páginas del
            prototipo navegable (`admin.jsx:3918-3923` saltaba entre los
            `.dc.html`), y habían llegado acá sin `onClick`, como cascarón
            visual. Con ellos se va también el `zIndex: 20`, que solo existía
            para que `<main>` (posterior en el orden del documento) no los
            tapara en pantallas con una columna sticky como
            `/welcome-screen`: ya no queda nada superpuesto a `<main>`, así
            que el workaround perdió su objeto. */}
          <main
            style={{
              flex: 1,
              minWidth: 0,
              padding: `${PORTAL_MAIN_PADDING.top}px ${PORTAL_MAIN_PADDING.x}px ${PORTAL_MAIN_PADDING.bottom}px`,
            }}
          >
            <Outlet />
          </main>
        </div>
      </div>
    </IdleSessionGuard>
  );
}
